import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import './reports.css';

const DoctorRevenue = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [revenueData, setRevenueData] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDoctors();
  }, [token, i18n.language]);

  useEffect(() => {
    if (selectedDoctor && dateRange.startDate && dateRange.endDate) {
      fetchDoctorRevenue();
    }
  }, [selectedDoctor, dateRange, token, i18n.language]);

  const fetchDoctors = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/doctores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        setDoctors(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchDoctorRevenue = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/doctor_revenue_report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({
          doctor_id: selectedDoctor,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }),
      });

      const result = await response.json();
      if (result.status) {
        setRevenueData(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch revenue data');
      }
    } catch (error) {
      setError('Error fetching revenue data');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  const calculateTotals = () => {
    const totals = revenueData.reduce((acc, item) => {
      acc.totalRevenue += parseFloat(item.total_revenue) || 0;
      acc.doctorShare += parseFloat(item.doctor_share) || 0;
      acc.clinicShare += parseFloat(item.clinic_share) || 0;
      acc.totalAppointments += parseInt(item.appointments_count) || 0;
      return acc;
    }, {
      totalRevenue: 0,
      doctorShare: 0,
      clinicShare: 0,
      totalAppointments: 0
    });

    return totals;
  };

  const selectedDoctorData = doctors.find(doc => doc.id == selectedDoctor);

  // if (loading) {
  //   return (
  //     <div className="container tables bg-white mt-5">
  //       <div className="text-center">
  //         <div className="spinner-border" role="status">
  //           <span className="visually-hidden">Loading...</span>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  const totals = calculateTotals();

  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('reports.doctorRevenue.title')}</h3>
      </div>

      <div className="report-filters">
        <div className="row">
          <div className="col-md-3">
            <label className="form-label">{t('reports.selectDoctor')}</label>
            <select
              className="form-control"
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
            >
              <option value="">{t('reports.selectDoctor')}</option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name} ({doctor.revenue || 0}%)
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('reports.startDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('reports.endDate')}</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {selectedDoctor && selectedDoctorData && (
        <div className="doctor-info">
          <h4>{selectedDoctorData.name}</h4>
          <p>{t('reports.revenuePercentage')}: {selectedDoctorData.revenue || 0}%</p>
        </div>
      )}

      {selectedDoctor && revenueData.length > 0 && (
        <div className="report-summary">
          <div className="row">
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalRevenue')}</h5>
                <span className="summary-number">{formatCurrency(totals.totalRevenue)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.doctorShare')}</h5>
                <span className="summary-number">{formatCurrency(totals.doctorShare)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.clinicShare')}</h5>
                <span className="summary-number">{formatCurrency(totals.clinicShare)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="summary-card">
                <h5>{t('reports.totalAppointments')}</h5>
                <span className="summary-number">{totals.totalAppointments}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedDoctor && (
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{t('reports.date')}</th>
                <th>{t('reports.appointmentsCount')}</th>
                <th>{t('reports.totalRevenue')}</th>
                <th>{t('reports.doctorShare')}</th>
                <th>{t('reports.clinicShare')}</th>
                <th>{t('reports.revenuePercentage')}</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    {t('reports.noRevenueData')}
                  </td>
                </tr>
              ) : (
                revenueData.map((item) => (
                  <tr key={item.date}>
                    <td>{formatDate(item.date)}</td>
                    <td>{item.appointments_count}</td>
                    <td>{formatCurrency(item.total_revenue)}</td>
                    <td>{formatCurrency(item.doctor_share)}</td>
                    <td>{formatCurrency(item.clinic_share)}</td>
                    <td>{selectedDoctorData?.revenue || 0}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DoctorRevenue;
